/* Slideshow optimized for performance
   - Lazy initializes when visible
   - Mobile uses slide (lighter)
   - Desktop keeps fade
   - Avoids blocking main thread
*/

if (!customElements.get("m-slideshow")) {
  class MSlideshow extends HTMLElement {
    constructor() {
      super();
      this.slider = null;
      this.observer = null;
    }

    connectedCallback() {
      this.autoplay = this.dataset.autoplay === "true";
      this.autoplaySpeed = parseInt(this.dataset.autoplaySpeed) || 5;
      this.showArrows = this.dataset.enableArrows === "true";
      this.showDots = this.dataset.enableDots === "true";
      this.id = this.dataset.id;
      this.sliderContainer = this.querySelector(".swiper-container");

      this.lastVideo = null;
      this.lastActive = null;

      /* Lazy init slider only when visible */
      this.initWhenVisible();
    }

    initWhenVisible() {
      if (!("IntersectionObserver" in window)) {
        this.initSlider();
        return;
      }

      this.observer = new IntersectionObserver(
        (entries) => {
          if (entries[0].isIntersecting) {
            this.initSlider();
            this.observer.disconnect();
          }
        },
        { rootMargin: "0px 0px 200px 0px" } // preload slightly before visible
      );

      this.observer.observe(this);
    }

    initSlider() {
      if (this.slider || !this.sliderContainer) return;

      const isMobile = window.matchMedia("(max-width: 767px)").matches;

      this.slider = new MinimogLibs.Swiper(this.sliderContainer, {
        init: false,

        /* Autoplay starts only after init */
        autoplay: this.autoplay
          ? {
              delay: this.autoplaySpeed * 1000,
              disableOnInteraction: true,
            }
          : false,

        slidesPerView: 1,
        slidesPerGroup: 1,
        loop: true,

        /* Mobile uses lighter animation */
        effect: isMobile ? "slide" : "fade",
        fadeEffect: !isMobile
          ? { crossFade: true }
          : undefined,

        /* Slightly faster animation to reduce CPU cost */
        speed: isMobile ? 500 : 700,

        navigation: this.showArrows
          ? {
              nextEl: this.querySelector(".m-slider-controls__button-next"),
              prevEl: this.querySelector(".m-slider-controls__button-prev"),
            }
          : false,

        pagination: this.showDots
          ? {
              el: this.querySelector(".swiper-pagination"),
              clickable: true,
              bulletClass: "m-dot",
              bulletActiveClass: "m-dot--active",
            }
          : false,

        on: {
          init: () => {
            /* Avoid resize on mobile to reduce blocking */
            if (!isMobile) {
              setTimeout(() => {
                window.dispatchEvent(new Event("resize"));
              }, 100);
            }
          },
        },
      });

      this.slider.on("init", this.handleChange.bind(this));
      this.slider.on("slideChange", this.handleChange.bind(this));
      this.slider.init();

      if (Shopify.designMode) {
        document.addEventListener("shopify:block:select", (e) =>
          this.onBlockSelect(e)
        );
      }
    }

    handleChange(swiper) {
      const { activeIndex, slides } = swiper;
      const slideType =
        slides[activeIndex] && slides[activeIndex].dataset.slideType;

      if (this.lastActive !== null) {
        this.slider.slideToLoop(this.lastActive);
        this.lastActive = null;
      }

      if (slideType === "video_slide") {
        const video =
          slides[activeIndex] && slides[activeIndex].querySelector("video");

        if (video) {
          this.lastVideo && this.lastVideo.pause();
          const playPromise = video.play();
          if (playPromise !== undefined) {
            playPromise.then(() => {
              this.lastVideo = video;
            });
          }
        }
      } else {
        this.lastVideo && this.lastVideo.pause();
        this.lastVideo = null;
      }
    }

    onBlockSelect(ev) {
      const block = ev.target;
      const index = Number(block.dataset.slide);
      this.lastActive = index;

      if (this.slider) {
        this.slider.slideToLoop(index);
      }
    }
  }

  customElements.define("m-slideshow", MSlideshow);
}